import PersonIcon from "@mui/icons-material/Person";
import { Box, CircularProgress, IconButton, Menu, MenuItem, TextField, Typography } from "@mui/material";
import type React from "react";
import { type ReactElement, useCallback, useId, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../contexts/AuthProvider";
import { useSnackbar } from "../contexts/SnackbarContext";
import { useLastNameFirst } from "../hooks/useNameFormat";
import { useUserSwitcher } from "../hooks/useUserSwitcher";
import { localStorageService } from "../services/LocalStorageService";
import { trackErrorEvent } from "../utils/analytics";
import { formatPersonFullName } from "../utils/formatters";
import type { ImpersonatableUser } from "../utils/models";
import axiosInstance from "../utils/networkWrapper";

/** The user's name in the department's name order (`name_format_last_first`). */
const impersonatableUserName = (u: ImpersonatableUser, lastNameFirst: boolean): string =>
	// DEFAULT: an account may have no first or last name; the missing part adds nothing, as the old `filter(Boolean)` join did.
	formatPersonFullName({ firstName: u.first_name ?? "", lastName: u.last_name ?? "" }, lastNameFirst);

export function UserSwitcher(): ReactElement | null {
	const { userInfo: user, impersonateUser, stopImpersonation } = useAuth();
	const { showSnackbar } = useSnackbar();
	const { t } = useTranslation();
	const lastNameFirst = useLastNameFirst();
	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
	const [loadingTarget, setLoadingTarget] = useState<string | null>(null);
	const [searchQuery, setSearchQuery] = useState("");
	const menuId = useId();
	const open = Boolean(anchorEl);

	// Check impersonation state - show UserSwitcher if user is support OR if impersonation is active
	const isImpersonating = !!user?.impersonated_by;
	const isSupportUser = !!user?.support;

	// Use the custom hook for fetching impersonatable users
	// Only fetch when user is support (not when impersonating, as we only show stop button)
	const { impersonatableUsers, isPending } = useUserSwitcher(
		isSupportUser && open, // enabled condition
	);

	const handleClose = useCallback(() => {
		setAnchorEl(null);
		setSearchQuery("");
	}, []);

	const handleClick = (event: React.MouseEvent<HTMLElement>) => {
		setAnchorEl(event.currentTarget);
	};

	// Filter and sort users based on search query (case-insensitive, checks name, username and phone)
	const filteredUsers = useMemo(() => {
		const query = searchQuery.toLowerCase();
		const filtered = impersonatableUsers.filter((u) => {
			// Both name orders, so the search matches whichever order is displayed.
			const fullNames = [impersonatableUserName(u, false), impersonatableUserName(u, true)].map((name) => name.toLowerCase());
			return (
				fullNames.some((name) => name.includes(query)) ||
				u.first_name?.toLowerCase().includes(query) ||
				u.last_name?.toLowerCase().includes(query) ||
				u.username?.toLowerCase().includes(query) ||
				u.phone_number?.includes(query)
			);
		});
		return filtered.sort((a, b) => {
			const aFullName = impersonatableUserName(a, lastNameFirst) || a.first_name || a.username || a.phone_number || "";
			const bFullName = impersonatableUserName(b, lastNameFirst) || b.first_name || b.username || b.phone_number || "";
			return aFullName.localeCompare(bFullName);
		});
	}, [impersonatableUsers, searchQuery, lastNameFirst]);

	// Opens the login by id: phones are shared between logins, so the phone could open a different account.
	const handleImpersonateUser = async (userId: string, userIdentifier: string) => {
		try {
			setLoadingTarget(userId);
			showSnackbar(t("switching.to.user", { userIdentifier }), "info");
			const chosenDeptId = localStorageService.getChosenDepartmentID();
			const url = chosenDeptId
				? `/impersonation/user/${encodeURIComponent(userId)}?department_id=${chosenDeptId}`
				: `/impersonation/user/${encodeURIComponent(userId)}`;
			const response = await axiosInstance.post(url);
			await impersonateUser(response.data.access_token);
			handleClose();
		} catch (error) {
			console.error("Error impersonating user:", error);
			trackErrorEvent("error impersonating user", error, {
				action: "error impersonating user",
			});
			showSnackbar(t("error.switch.user"), "error");
		} finally {
			setLoadingTarget(null);
		}
	};

	const handleStopImpersonation = async () => {
		try {
			setLoadingTarget("stop-impersonation");
			showSnackbar(t("stopping.impersonation"), "info");
			await stopImpersonation();
			handleClose();
		} catch (error) {
			console.error("Error stopping impersonation:", error);
			showSnackbar(t("error.stop.impersonation"), "error");
		} finally {
			setLoadingTarget(null);
		}
	};

	// Don't render anything if user is not logged in or doesn't have permission
	// Show UserSwitcher if user is support OR if impersonation is active
	if (!user || (!isSupportUser && !isImpersonating)) {
		return null;
	}

	return (
		<>
			<IconButton
				onClick={handleClick}
				size="small"
				data-testid="user-switcher-button"
				aria-controls={open ? menuId : undefined}
				aria-haspopup="true"
				aria-expanded={open ? "true" : undefined}
				disabled={!!loadingTarget}
			>
				{loadingTarget ? <CircularProgress size={24} /> : <PersonIcon />}
			</IconButton>
			<Menu
				id={menuId}
				anchorEl={anchorEl}
				open={open}
				onClose={handleClose}
				slotProps={{
					paper: {
						sx: { minWidth: 300 },
					},
					list: {
						"aria-labelledby": "user-switcher-button",
					},
				}}
			>
				<Box sx={{ px: 2, py: 1 }}>
					<Typography variant="subtitle2">{user?.username}</Typography>
					{user?.impersonated_by && (
						<Typography
							variant="caption"
							sx={{
								color: "text.secondary",
							}}
						>
							Impersonated by {user.impersonated_by}
						</Typography>
					)}
				</Box>
				{isImpersonating
					? [
							<MenuItem
								key={"stop-impersonation"}
								onClick={handleStopImpersonation}
								disabled={!!loadingTarget}
								data-testid="user-switcher-stop"
								sx={{
									borderTop: 1,
									borderColor: "divider",
									mt: 1,
								}}
							>
								{loadingTarget === "stop-impersonation" ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
								Stop Impersonation
							</MenuItem>,
						]
					: [
							<Box key={"search-box"} sx={{ px: 2, pt: 1, pb: 1 }}>
								<TextField
									size="small"
									fullWidth
									placeholder="Search users..."
									value={searchQuery}
									onChange={(e) => setSearchQuery(e.target.value)}
									onKeyDown={(e) => {
										e.stopPropagation();
									}}
									slotProps={{ htmlInput: { "data-testid": "user-switcher-search" } }}
									autoFocus
								/>
							</Box>,
							<MenuItem
								key={"switch-to-user-label"}
								sx={{
									borderTop: 1,
									borderColor: "divider",
								}}
								disabled
							>
								Switch to User
							</MenuItem>,
							isPending ? (
								<MenuItem key={"loading-users"} disabled>
									<CircularProgress size={16} sx={{ mr: 1 }} />
									Loading users...
								</MenuItem>
							) : filteredUsers.length === 0 ? (
								<MenuItem key={"no-users-found"} disabled>
									<Typography
										variant="body2"
										sx={{
											color: "text.secondary",
										}}
									>
										No users found
									</Typography>
								</MenuItem>
							) : (
								filteredUsers.map((impersonatableUser) => {
									const userIdentifier = impersonatableUser.username || impersonatableUser.phone_number || "";
									const fullName = impersonatableUserName(impersonatableUser, lastNameFirst);
									const displayName = fullName || impersonatableUser.first_name || impersonatableUser.username || impersonatableUser.phone_number;
									const displaySubtext =
										fullName || impersonatableUser.first_name
											? impersonatableUser.username || impersonatableUser.phone_number
											: impersonatableUser.username
												? impersonatableUser.phone_number
												: null;

									return (
										<MenuItem
											key={impersonatableUser.id}
											data-testid="user-switcher-user-item"
											onClick={() => handleImpersonateUser(impersonatableUser.id, userIdentifier)}
											disabled={!!loadingTarget}
										>
											{loadingTarget === impersonatableUser.id ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
											<Box sx={{ display: "flex", flexDirection: "column", flex: 1 }}>
												<Typography variant="body2">{displayName}</Typography>
												{displaySubtext && (
													<Typography
														variant="caption"
														sx={{
															color: "text.secondary",
														}}
													>
														{displaySubtext}
													</Typography>
												)}
											</Box>
										</MenuItem>
									);
								})
							),
						]}
			</Menu>
		</>
	);
}
